import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useMachineOptions } from '../select/MachineSelect';
import { SubprocessOption } from '../select/SubprocessCells';
import { formatRU } from './RNSubprocessMachinesTable';

interface Props {
  activeSubprocess?: SubprocessOption;
}

const COLUMNS: Array<[string, number]> = [['machine', 200], ['ruAssembly', 230], ['ruHandling', 230]];
const cellClass = 'border border-gray-300 h-[30px] px-1.5 text-[11px] text-gray-700';

// Норматив: машините на подпроцесот, само за читање. Одобрување и поправки се на РН.
const OSSubprocessMachinesTable: React.FC<Props> = ({ activeSubprocess }) => {
  const { t, i18n } = useTranslation();
  const machineOptions = useMachineOptions();
  const [show, setShow] = useState(true);

  const rows = useMemo(() => {
    const machineById = new Map(machineOptions.map((machine) => [machine._id, machine]));
    return [...(activeSubprocess?.machines || [])]
      .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
      .map((entry) => {
        const ref = entry.machine;
        const machineId = typeof ref === 'object' && ref ? ref._id : String(ref || '');
        return { machineId, machine: machineById.get(machineId) };
      })
      .filter((row) => !!row.machineId);
  }, [activeSubprocess, machineOptions]);

  if (!activeSubprocess) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-4 max-w-full">
      <button
        type="button"
        onClick={() => setShow((value) => !value)}
        aria-expanded={show}
        title={show ? t('rnMachineRepairs.hideMachines') || undefined : t('rnMachineRepairs.showMachines') || undefined}
        className={`themed-gradient w-full px-5 py-2 flex items-center gap-2 text-left focus:outline-none focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 ${show ? 'rounded-t-xl' : 'rounded-xl'}`}
      >
        {show ? <ChevronDown size={16} className="text-white" /> : <ChevronRight size={16} className="text-white" />}
        <h2 className="text-white font-semibold text-sm">
          {t('rnMachineRepairs.machinesTitle')} — {activeSubprocess.name || ''}
        </h2>
      </button>
      {show && (
        <div className="overflow-x-auto rounded-b-xl">
          <table className="border-collapse w-full">
            <thead>
              <tr>
                {COLUMNS.map(([key, width]) => (
                  <th
                    key={key}
                    className="border border-gray-300 bg-gray-200 text-gray-700 font-semibold text-center text-[11px] h-[32px] px-1.5"
                    style={{ minWidth: width }}
                  >
                    {t(`rnMachineRepairs.${key}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={`${cellClass} text-center text-gray-400`}>
                    {t('rnMachineRepairs.empty')}
                  </td>
                </tr>
              ) : rows.map(({ machineId, machine }) => (
                <tr key={machineId} className="hover:bg-blue-50">
                  <td className={cellClass}>{machine ? `${machine.code} - ${machine.name}` : machineId}</td>
                  <td className={cellClass}>{formatRU(machine?.ru, i18n.language)}</td>
                  <td className={cellClass}>{formatRU(machine?.ruHandling, i18n.language)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OSSubprocessMachinesTable;
